import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useT } from "../i18n/LanguageContext";
import { gtagConsentUpdate } from "../lib/gtag";

// Stores only non-sensitive GDPR cookie-consent preferences (no personal data).
const KEY = "ybl-cookie-consent";

export function getConsent() {
  try {
    return JSON.parse(localStorage.getItem(KEY));
  } catch {
    return null;
  }
}

export default function CookieBanner() {
  const t = useT();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [optional, setOptional] = useState(true);

  useEffect(() => {
    if (!getConsent()) setVisible(true);
  }, []);

  // Lock scrolling while the cookie wall is up (no access without a choice).
  useEffect(() => {
    document.body.style.overflow = visible ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [visible]);

  const save = (consent) => {
    localStorage.setItem(KEY, JSON.stringify({ ...consent, date: new Date().toISOString() }));
    gtagConsentUpdate(!!consent.optional);
    setVisible(false);
  };

  return (
    <>
      {visible && (
        <>
          {/* Blocking wall — site is not accessible until a choice is made */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[99] bg-black/80 backdrop-blur-sm"
            data-testid="cookie-wall"
          />
          <motion.div
            data-testid="cookie-banner"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
            className="fixed bottom-4 left-4 right-4 md:left-8 md:right-auto md:w-[440px] backdrop-blur-2xl bg-[#1A1A1A]/95 border border-[#C5A059]/30 p-6 z-[100] shadow-2xl"
          >
            <p className="overline mb-3">{t("cookie.overline")}</p>
            <p className="text-white/70 text-sm leading-relaxed">
              {t("cookie.body")}{" "}
              <Link to="/cookie-policy" target="_blank" rel="noopener noreferrer" data-testid="cookie-policy-link" className="text-[#C5A059] underline underline-offset-2">{t("cookie.policyLink")}</Link>.
            </p>

            <AnimatePresence>
              {showPrefs && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 space-y-3 text-sm">
                    <label className="flex items-center justify-between text-white/60">
                      <span>{t("cookie.technicalLabel")}</span>
                      <input type="checkbox" checked disabled className="accent-[#C5A059]" />
                    </label>
                    <label className="flex items-center justify-between text-white/80 cursor-pointer">
                      <span>{t("cookie.analyticsLabel")}</span>
                      <input
                        data-testid="cookie-optional-toggle"
                        type="checkbox"
                        checked={optional}
                        onChange={(e) => setOptional(e.target.checked)}
                        className="accent-[#C5A059]"
                      />
                    </label>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex flex-col gap-2 mt-5">
              <button
                data-testid="cookie-accept-all"
                onClick={() => save({ technical: true, optional: true })}
                className="bg-[#C5A059] text-[#0A0A0A] py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] transition-colors"
              >
                {t("cookie.acceptAll")}
              </button>
              <div className="flex gap-2">
                <button
                  data-testid="cookie-reject"
                  onClick={() => save({ technical: true, optional: false })}
                  className="flex-1 border border-white/20 text-white/80 py-3 text-xs uppercase tracking-[0.2em] hover:border-white/50 transition-colors"
                >
                  {t("cookie.technicalOnly")}
                </button>
                {showPrefs ? (
                  <button
                    data-testid="cookie-save-prefs"
                    onClick={() => save({ technical: true, optional })}
                    className="flex-1 border border-[#C5A059] text-[#C5A059] py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors"
                  >
                    {t("cookie.save")}
                  </button>
                ) : (
                  <button
                    data-testid="cookie-customize"
                    onClick={() => setShowPrefs(true)}
                    className="flex-1 border border-white/20 text-white/80 py-3 text-xs uppercase tracking-[0.2em] hover:border-white/50 transition-colors"
                  >
                    {t("cookie.customize")}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </>
  );
}
